import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { colors } from '../styles/theme';
import {
  CENTERED,
  clampOffset,
  isCentered,
  readOffset,
  sameOffset,
  saveOffset,
  type PopupOffset,
} from './popupPosition.helpers';

/** How far one press of an arrow key moves the popup. */
const KEY_STEP_PX = 24;

const ARROW_STEPS = new Map<string, PopupOffset>([
  ['ArrowLeft', { x: -KEY_STEP_PX, y: 0 }],
  ['ArrowRight', { x: KEY_STEP_PX, y: 0 }],
  ['ArrowUp', { x: 0, y: -KEY_STEP_PX }],
  ['ArrowDown', { x: 0, y: KEY_STEP_PX }],
]);

// One position for every popup on the page, so a popup that stays mounted and one that is
// built on opening agree on where the person wants them. Read from the browser once.
let kept: PopupOffset | null = null;
const watchers = new Set<() => void>();

function currentOffset(): PopupOffset {
  kept ??= readOffset();
  return kept;
}

function moveTo(next: PopupOffset): void {
  if (sameOffset(currentOffset(), next)) return;
  kept = next;
  watchers.forEach((notify) => notify());
}

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

interface Drag {
  pointerX: number;
  pointerY: number;
  from: PopupOffset;
}

export interface MovablePopup<T extends HTMLElement> {
  /** Goes on the popup's own box (not on the dimmed layer behind it). */
  panelRef: RefObject<T>;
  /** Merged into that box's style. */
  panelStyle: CSSProperties;
  /** The strip to drag it by, with the way back to the centre. First child of the box. */
  bar: JSX.Element;
}

/**
 * A popup opens centred, can be dragged anywhere inside the window, opens next time
 * where it was left, and goes back to the centre with "Reset position".
 *
 * The box keeps whatever centres it; this only adds the distance it was moved by.
 */
export function useMovablePopup<T extends HTMLElement>(): MovablePopup<T> {
  const panelRef = useRef<T>(null);
  // Markup rendered ahead of the browser has no kept position: it is centred.
  const offset = useSyncExternalStore(watch, currentOffset, () => CENTERED);
  const drag = useRef<Drag | null>(null);
  const sizeWatcher = useRef<ResizeObserver | null>(null);
  const watched = useRef<T | null>(null);

  const fit = useCallback((wanted: PopupOffset): PopupOffset => {
    const panel = panelRef.current;
    const box = panel?.getBoundingClientRect();
    // Not on screen yet (a dialog before it opens): nothing to measure against.
    if (!panel || !box || box.width === 0 || box.height === 0) return wanted;
    // Where the box sits unmoved: where it is now, less the move it carries now. Read
    // from the page, which a fast drag can be one paint behind what was last asked for.
    const carried = new DOMMatrixReadOnly(globalThis.getComputedStyle(panel).transform);
    return clampOffset(
      wanted,
      {
        left: box.left - carried.m41,
        top: box.top - carried.m42,
        width: box.width,
        height: box.height,
      },
      { width: globalThis.innerWidth, height: globalThis.innerHeight },
    );
  }, []);

  // Pulled back inside whenever the box or the window changes size — which is also
  // what happens the moment it first appears.
  useEffect(() => {
    const refit = (): void => moveTo(fit(currentOffset()));
    globalThis.addEventListener('resize', refit);
    sizeWatcher.current = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refit);
    return () => {
      globalThis.removeEventListener('resize', refit);
      sizeWatcher.current?.disconnect();
      sizeWatcher.current = null;
      watched.current = null;
    };
  }, [fit]);

  // Some popups are built on opening; others stay mounted and show their box only while
  // open. Either way the box on the page now is the one whose size is watched.
  useEffect(() => {
    const box = panelRef.current;
    if (box === watched.current) return;
    if (watched.current) sizeWatcher.current?.unobserve(watched.current);
    watched.current = box;
    if (box) sizeWatcher.current?.observe(box);
  });

  const startDrag = (event: PointerEvent<HTMLButtonElement>): void => {
    if (event.button !== 0) return;
    // Keeps the drag alive when the pointer leaves the strip.
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    drag.current = { pointerX: event.clientX, pointerY: event.clientY, from: currentOffset() };
  };

  const dragTo = (event: PointerEvent<HTMLButtonElement>): void => {
    const started = drag.current;
    if (!started) return;
    moveTo(
      fit({
        x: started.from.x + event.clientX - started.pointerX,
        y: started.from.y + event.clientY - started.pointerY,
      }),
    );
  };

  const endDrag = (): void => {
    if (!drag.current) return;
    drag.current = null;
    saveOffset(currentOffset());
  };

  const nudge = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const step = ARROW_STEPS.get(event.key);
    if (!step) return;
    event.preventDefault();
    const from = currentOffset();
    const next = fit({ x: from.x + step.x, y: from.y + step.y });
    moveTo(next);
    saveOffset(next);
  };

  const reset = (): void => {
    moveTo(CENTERED);
    saveOffset(CENTERED);
  };

  const bar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <button
        type="button"
        aria-label="Move this window"
        title="Drag to move"
        onPointerDown={startDrag}
        onPointerMove={dragTo}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={nudge}
        style={{
          flex: 1,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'move',
          touchAction: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{ width: 40, height: 4, borderRadius: 2, background: colors.muted, opacity: 0.4 }}
        />
      </button>
      {!isCentered(offset) && (
        <button
          type="button"
          onClick={reset}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontSize: 11,
            color: colors.muted,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Reset position
        </button>
      )}
    </div>
  );

  return {
    panelRef,
    panelStyle: isCentered(offset) ? {} : { transform: `translate(${offset.x}px, ${offset.y}px)` },
    bar,
  };
}
